<template>
  <div class="p-0">
    <div class="mb-5">
      <router-link :to="backPath" class="inline-flex items-center text-sm font-semibold text-gray-600 hover:text-gray-900">
        <i class="fa-solid fa-arrow-left mr-2"></i>
        {{ isManagerConsult ? 'Volver a bandeja' : 'Volver a mis solicitudes' }}
      </router-link>
    </div>

    <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">{{ isManagerConsult ? 'Cupos del docente' : 'Mis cupos' }}</h1>
        <p class="mt-1 text-sm text-gray-600">
          {{ isManagerConsult ? `Disponibilidad de ${teacherName} por articulo activo.` : 'Disponibilidad por artículo según licencias usadas y pendientes.' }}
        </p>
      </div>
      <router-link
        :to="newRequestLink"
        class="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold bg-indigo-700 text-white rounded-md hover:bg-indigo-800"
      >
        + Nueva solicitud
      </router-link>
    </div>

    <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-5">
      <div class="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div class="md:col-span-2">
          <label class="block text-xs font-semibold text-gray-500 mb-1">Fecha de referencia</label>
          <input v-model="referenceDate" type="date" class="field" />
        </div>
        <div class="md:col-span-2">
          <label class="block text-xs font-semibold text-gray-500 mb-1">Buscar artículo</label>
          <input v-model="search" type="text" class="field" placeholder="Ej: ART_74, Exámenes..." />
        </div>
        <div class="flex items-end">
          <button
            type="button"
            @click="fetchBalances"
            class="w-full px-4 py-2 text-sm font-semibold bg-indigo-700 text-white rounded-md hover:bg-indigo-800"
          >
            Actualizar
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="py-10">
      <Loading />
    </div>
    <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
      {{ error }}
      <button type="button" @click="fetchBalances" class="ml-2 underline font-semibold">Reintentar</button>
    </div>
    <div v-else-if="filteredBalances.length === 0" class="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center">
      <h2 class="text-lg font-semibold text-gray-900">No hay cupos para mostrar</h2>
      <p class="mt-1 text-sm text-gray-500">Probá cambiar la búsqueda o consultá a administración si no hay artículos activos.</p>
    </div>

    <div v-else class="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <article
        v-for="balance in filteredBalances"
        :key="balance.leave_article_id || balance.article_id || balance.article_code"
        class="bg-white border border-gray-200 rounded-xl shadow-sm p-5"
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <span class="inline-flex px-2 py-1 text-xs font-semibold rounded bg-indigo-50 text-indigo-700">
              {{ balance.article_code || '-' }}
            </span>
            <h2 class="mt-3 text-lg font-semibold text-gray-900">{{ balanceTitle(balance) }}</h2>
            <p class="text-sm text-gray-500">{{ periodLabel(balance) }}</p>
          </div>
          <span
            class="px-2 py-1 text-xs font-semibold rounded-full border"
            :class="balance.can_request ? 'bg-green-50 text-green-700 border-green-100' : 'bg-red-50 text-red-700 border-red-100'"
          >
            {{ balance.can_request ? 'Disponible para solicitar' : 'Sin cupo' }}
          </span>
        </div>

        <div class="mt-5">
          <div class="flex justify-between text-sm font-semibold text-gray-700">
            <span>Progreso de uso</span>
            <span>{{ progressText(balance) }}</span>
          </div>
          <div v-if="hasNumericLimit(balance)" class="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div class="h-full bg-indigo-800 rounded-full" :style="{ width: `${progressPercent(balance)}%` }"></div>
          </div>
          <div v-else class="mt-2 border border-dashed border-gray-300 rounded-md p-4 text-center text-sm text-gray-600">
            <i class="fa-solid fa-infinity mr-2"></i>
            Sin límite numérico configurado
          </div>
        </div>

        <div class="mt-5 grid grid-cols-2 gap-3">
          <div class="bg-gray-50 rounded-md p-3">
            <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Usados</div>
            <div class="mt-1 text-lg font-bold text-gray-900">{{ balance.used_days ?? 0 }}</div>
          </div>
          <div class="bg-indigo-50 rounded-md p-3">
            <div class="text-xs font-semibold uppercase tracking-wide text-indigo-500">Disponibles</div>
            <div class="mt-1 text-lg font-bold text-indigo-900">{{ availableText(balance) }}</div>
          </div>
        </div>

        <div class="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-gray-100 pt-4">
          <p class="text-sm" :class="balance.can_request ? 'text-green-700' : 'text-red-700'">
            {{ balance.message || (balance.can_request ? 'Podés solicitar este artículo.' : 'No disponible para nuevas solicitudes.') }}
          </p>
          <button
            v-if="balance.can_request"
            type="button"
            @click="startRequest(balance)"
            class="px-4 py-2 text-sm font-semibold text-indigo-700 bg-indigo-50 rounded-md hover:bg-indigo-100 whitespace-nowrap"
          >
            Nueva solicitud <i class="fa-solid fa-arrow-right ml-2"></i>
          </button>
        </div>
      </article>

      <p class="xl:col-span-2 text-center text-xs text-gray-500">Mostrando {{ filteredBalances.length }} artículos activos</p>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/commons/Loading.vue';
import { leaveAvailabilityApi } from '@/service/leave.service';
import { teachersApi } from '@/service/user.service';
import { categoryLabel, teacherFullName, todayISO, unwrapData, unwrapList } from '@/utils/leave';

export default {
  name: 'LeaveBalancesView',
  components: { Loading },
  data() {
    return {
      loading: false,
      error: '',
      referenceDate: this.$route.query.date || todayISO(),
      search: '',
      balances: [],
      teacher: null,
    };
  },
  computed: {
    currentUserId() {
      return this.$store.getters['authentication/currentUser']?.user_id;
    },
    targetUserId() {
      return this.$route.query.user_id || this.currentUserId;
    },
    isManagerConsult() {
      return Boolean(this.$route.query.user_id && this.$route.query.user_id !== this.currentUserId);
    },
    teacherName() {
      return this.teacher ? teacherFullName(this.teacher) : 'docente seleccionado';
    },
    backPath() {
      return this.isManagerConsult ? '/leave/inbox' : '/leave/requests';
    },
    newRequestLink() {
      return this.isManagerConsult
        ? { path: '/leave/requests/new', query: { as: 'manager', user_id: this.targetUserId } }
        : '/leave/requests/new';
    },
    filteredBalances() {
      const term = this.search.trim().toLowerCase();
      if (!term) return this.balances;
      return this.balances.filter((balance) =>
        [
          balance.article_code,
          balance.article_name,
          balance.name,
          balance.category,
          balance.message,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(term)
      );
    },
  },
  created() {
    this.fetchTeacher();
    this.fetchBalances();
  },
  methods: {
    async fetchTeacher() {
      if (!this.isManagerConsult) return;
      try {
        const response = await teachersApi.get(this.targetUserId);
        this.teacher = unwrapData(response);
      } catch {
        this.teacher = null;
      }
    },
    async fetchBalances() {
      if (!this.targetUserId) return;
      this.loading = true;
      this.error = '';
      try {
        const response = await leaveAvailabilityApi.balances(this.targetUserId, {
          date: this.referenceDate,
        });
        this.balances = unwrapList(response);
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudieron cargar los cupos.';
      } finally {
        this.loading = false;
      }
    },
    balanceTitle(balance) {
      return balance.article_name || balance.name || balance.article_code || 'Artículo';
    },
    periodLabel(balance) {
      const period = {
        calendar_year: 'Año calendario',
        school_year: 'Año lectivo',
        month: 'Mensual',
        event: 'Por evento',
        no_limit: 'Sin límite',
      }[balance.period_type] || categoryLabel(balance.category);
      const year = this.referenceDate ? this.referenceDate.slice(0, 4) : '';
      return [period, year].filter(Boolean).join(' ');
    },
    hasNumericLimit(balance) {
      return balance.available_days !== null && balance.available_days !== undefined;
    },
    totalDays(balance) {
      return Number(balance.used_days || 0) + Number(balance.available_days || 0);
    },
    progressPercent(balance) {
      const total = this.totalDays(balance);
      if (!total) return 0;
      return Math.min(100, Math.round((Number(balance.used_days || 0) / total) * 100));
    },
    progressText(balance) {
      if (!this.hasNumericLimit(balance)) return `${balance.used_days || 0} usos registrados`;
      return `${balance.used_days || 0} de ${this.totalDays(balance)} días`;
    },
    availableText(balance) {
      return this.hasNumericLimit(balance) ? balance.available_days : 'Sin límite';
    },
    startRequest(balance) {
      const articleId = balance.leave_article_id || balance.article_id;
      this.$router.push({
        path: '/leave/requests/new',
        query: {
          ...(articleId ? { article_id: articleId } : {}),
          ...(this.isManagerConsult ? { as: 'manager', user_id: this.targetUserId } : {}),
        },
      });
    },
  },
};
</script>

<style scoped>
.field {
  @apply w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600;
}
</style>
